/**
 * Brand Memory + Brand Kit Redux slice.
 *
 * One slice covers both resources because they're co-managed in the UI
 * (Brand Kit list + asset lists live in the same page). Each resource has
 * its own status/error state to allow granular loading indicators.
 *
 * Maps 1-to-1 to backend router /v1/domains/{id}/... — see
 * api.refinea.io/app/routers/knowledge_base.py.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';
import type {
  BrandKit,
  CreateBrandKitPayload,
  UpdateBrandKitPayload,
  ProofPoint,
  CreateProofPointPayload,
  UpdateProofPointPayload,
  ExpertVoice,
  CreateExpertVoicePayload,
  UpdateExpertVoicePayload,
  CreateQuotePayload,
  UpdateQuotePayload,
  FactStatistic,
  CreateFactPayload,
  UpdateFactPayload,
  SitePageEnriched,
  SiteCorpusManifest,
  SiteCorpusPage,
  SiteCorpusPageDetail,
  SiteCorpusPagesResponse,
  SiteCorpusRole,
  SiteIdentity,
  SiteIdentityProposal,
  SaveSiteIdentityPayload,
  BuildStatus,
  StatusType,
} from '@/types/brandMemory';
import { isShowcase } from './showcaseGate';

type Loadable = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface BrandMemoryState {
  // Brand Kits
  brandKits: BrandKit[];
  brandKitsStatus: Loadable;
  brandKitsError: string | null;

  // Proof Points
  proofPoints: ProofPoint[];
  proofPointsStatus: Loadable;
  proofPointsError: string | null;

  // Expert Voices
  expertVoices: ExpertVoice[];
  expertVoicesStatus: Loadable;
  expertVoicesError: string | null;

  // Facts & Statistics
  facts: FactStatistic[];
  factsStatus: Loadable;
  factsError: string | null;

  // Site Pages
  sitePages: SitePageEnriched[];
  sitePagesStatus: Loadable;

  // Build status (full KB)
  buildStatus: BuildStatus | null;
  buildStatusLoadable: Loadable;
  buildTriggering: boolean;
  buildTriggerError: string | null;

  // Build status (Brand Kit standalone refresh — independent from the KB
  // build above; the two can be in-flight in parallel without interfering)
  brandKitBuildStatus: BuildStatus | null;
  brandKitBuildStatusLoadable: Loadable;
  brandKitBuildTriggering: boolean;
  brandKitBuildTriggerError: string | null;

  // ── Site corpus ──────────────────────────────────────────────────
  // The customer's own site, read page by page. Its own build status doc,
  // like the Brand Kit refresh, so three panels can be in flight at once
  // without one overwriting another's progress.
  siteCorpusManifest: SiteCorpusManifest | null;
  siteCorpusManifestStatus: Loadable;
  siteCorpusPages: SiteCorpusPage[];
  siteCorpusPagesStatus: Loadable;
  /** Null when there is no further page. Feeds "Load more". */
  siteCorpusNextCursor: string | null;
  siteCorpusFilters: { role: SiteCorpusRole | 'content' | 'any'; q: string };
  /** One entry per page the user has opened, keyed by url_hash: a second
   *  look at the same page costs nothing. */
  siteCorpusPageDetails: Record<string, SiteCorpusPageDetail>;
  siteCorpusPageDetailStatus: Loadable;
  siteCorpusBuildStatus: BuildStatus | null;
  siteCorpusBuildStatusLoadable: Loadable;
  siteCorpusBuildTriggering: boolean;
  siteCorpusBuildTriggerError: string | null;

  /** The site identity card (Brand Memory > Site). */
  siteIdentity: SiteIdentity | null;
  siteIdentityStatus: Loadable;
  /** A proposal that came back but was NOT stored (an approved card exists):
   *  the form opens on it and the person decides. */
  siteIdentityProposal: SiteIdentityProposal | null;
  siteIdentityProposing: boolean;
  siteIdentitySaving: boolean;
  /** The engine's words on a refused save: the key schema.org does not know,
   *  or the dead profile URL. */
  siteIdentityError: string | null;

  /** Live Smart Import progress, keyed by asset type. Updated by polling
   *  /knowledge-base/import/{type}/status while submitting. */
  importStatusByType: Partial<Record<SmartImportAssetType, ImportStatus>>;

  /** Source asset ids whose Change Category request is in flight. The UI
   *  uses this to disable the kebab + show a spinner on that specific
   *  block without locking up the rest of the page. Strings instead of
   *  Set so the slice stays serialisable. */
  changeCategoryInflight: string[];

  /** Last-write metadata for the most recent successful Change Category.
   *  The notebook reads this to drive the "Saved · view in Facts" toast
   *  and to clear the toast on the next interaction. */
  lastChangeCategory: {
    sourceType: ChangeCategorySourceType;
    targetType: ChangeCategoryTargetType;
    newId: string;
    lossy_fields: string[];
    at: number;
  } | null;
}

const initialState: BrandMemoryState = {
  brandKits: [],
  brandKitsStatus: 'idle',
  brandKitsError: null,
  proofPoints: [],
  proofPointsStatus: 'idle',
  proofPointsError: null,
  expertVoices: [],
  expertVoicesStatus: 'idle',
  expertVoicesError: null,
  facts: [],
  factsStatus: 'idle',
  factsError: null,
  sitePages: [],
  sitePagesStatus: 'idle',
  siteCorpusManifest: null,
  siteCorpusManifestStatus: 'idle',
  siteCorpusPages: [],
  siteCorpusPagesStatus: 'idle',
  siteCorpusNextCursor: null,
  siteCorpusFilters: { role: 'content', q: '' },
  siteCorpusPageDetails: {},
  siteCorpusPageDetailStatus: 'idle',
  siteCorpusBuildStatus: null,
  siteCorpusBuildStatusLoadable: 'idle',
  siteCorpusBuildTriggering: false,
  siteCorpusBuildTriggerError: null,
  siteIdentity: null,
  siteIdentityStatus: 'idle',
  siteIdentityProposal: null,
  siteIdentityProposing: false,
  siteIdentitySaving: false,
  siteIdentityError: null,
  buildStatus: null,
  buildStatusLoadable: 'idle',
  buildTriggering: false,
  buildTriggerError: null,
  brandKitBuildStatus: null,
  brandKitBuildStatusLoadable: 'idle',
  brandKitBuildTriggering: false,
  brandKitBuildTriggerError: null,
  importStatusByType: {},
  changeCategoryInflight: [],
  lastChangeCategory: null,
};

// Helper to extract API error message in a consistent way. For 429 responses
// (rate-limit), the backend includes a `Retry-After` header (seconds) — we
// surface it to the user so they know when the limit window will roll back.
function errorMessage(err: unknown): string {
  const e = err as {
    response?: {
      status?: number;
      data?: { detail?: string; message?: string };
      headers?: Record<string, string>;
    };
    message?: string;
  };
  const baseMessage = e.response?.data?.detail || e.response?.data?.message || e.message || 'Request failed';
  if (e.response?.status === 429) {
    const retryAfter = e.response?.headers?.['retry-after'] || e.response?.headers?.['Retry-After'];
    const seconds = retryAfter ? parseInt(retryAfter, 10) : NaN;
    if (Number.isFinite(seconds) && seconds > 0) {
      const minutes = Math.ceil(seconds / 60);
      const human = minutes >= 60 ? `${Math.ceil(minutes / 60)}h` : `${minutes}m`;
      // If the backend already crafted a friendly message ("…Retry in ~5m
      // 30s."), don't append a second time-suggestion — keep what the server
      // said and just guarantee the time hint is at least visible.
      if (/retry in/i.test(baseMessage) || /try again/i.test(baseMessage)) return baseMessage;
      return `${baseMessage} Retry in ~${human}.`;
    }
  }
  return baseMessage;
}

/**
 * Unwrap a list endpoint response.
 *
 * The backend (api.refinea.io/app/controllers/KnowledgeBaseController.py) wraps
 * list payloads as `{ <key>: [...] }` (e.g. `{ "proof_points": [...] }`).
 * Some endpoints might return a bare array. This helper normalises both.
 */
function unwrapList<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const inner = (data as Record<string, unknown>)[key];
    if (Array.isArray(inner)) return inner as T[];
  }
  return [];
}

/**
 * Mount-time read gate. Every `fetchX` below is dispatched from a component
 * effect the moment its panel mounts; in the marketing showcase those panels
 * are mounted deliberately, against fixtures, with no auth and no API to
 * talk to. Blocking at `condition` (rather than at the transport) also keeps
 * a late `fulfilled` from overwriting the fixtures mid-screenshot.
 *
 * Writes (create/update/delete/trigger) are intentionally NOT gated: nothing
 * in the showcase clicks them, and gating them would silently swallow a real
 * user action if the flag ever leaked.
 */
const notInShowcase = <A,>(
  _arg: A,
  { getState }: { getState: () => unknown }
): boolean => !isShowcase(getState());

// ── Brand Kits ──────────────────────────────────────────────────────

export const fetchBrandKits = createAsyncThunk<BrandKit[], string, { rejectValue: string }>(
  'brandMemory/fetchBrandKits',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/brand-kits`);
      return unwrapList<BrandKit>(res.data, 'brand_kits');
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const createBrandKit = createAsyncThunk<
  BrandKit,
  { domainId: string; payload: CreateBrandKitPayload },
  { rejectValue: string }
>('brandMemory/createBrandKit', async ({ domainId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post<BrandKit>(`/v1/domains/${domainId}/brand-kits`, payload);
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const updateBrandKit = createAsyncThunk<
  BrandKit,
  { domainId: string; kitId: string; payload: UpdateBrandKitPayload },
  { rejectValue: string }
>('brandMemory/updateBrandKit', async ({ domainId, kitId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.patch<BrandKit>(`/v1/domains/${domainId}/brand-kits/${kitId}`, payload);
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const deleteBrandKit = createAsyncThunk<
  string,
  { domainId: string; kitId: string },
  { rejectValue: string }
>('brandMemory/deleteBrandKit', async ({ domainId, kitId }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/v1/domains/${domainId}/brand-kits/${kitId}`);
    return kitId;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const setPrimaryBrandKit = createAsyncThunk<
  BrandKit[],
  { domainId: string; kitId: string },
  { rejectValue: string }
>('brandMemory/setPrimaryBrandKit', async ({ domainId, kitId }, { rejectWithValue, dispatch }) => {
  try {
    await apiClient.post(`/v1/domains/${domainId}/brand-kits/${kitId}/set-primary`);
    // Refetch to get authoritative is_primary flags across all kits
    const refetch = await dispatch(fetchBrandKits(domainId)).unwrap();
    return refetch;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

// ── Proof Points ────────────────────────────────────────────────────

export const fetchProofPoints = createAsyncThunk<ProofPoint[], string, { rejectValue: string }>(
  'brandMemory/fetchProofPoints',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/knowledge-base/proof-points`);
      return unwrapList<ProofPoint>(res.data, 'proof_points');
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const createProofPoint = createAsyncThunk<
  ProofPoint,
  { domainId: string; payload: CreateProofPointPayload },
  { rejectValue: string }
>('brandMemory/createProofPoint', async ({ domainId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post<ProofPoint>(
      `/v1/domains/${domainId}/knowledge-base/proof-points`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const updateProofPoint = createAsyncThunk<
  ProofPoint,
  { domainId: string; ppId: string; payload: UpdateProofPointPayload },
  { rejectValue: string }
>('brandMemory/updateProofPoint', async ({ domainId, ppId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.patch<ProofPoint>(
      `/v1/domains/${domainId}/knowledge-base/proof-points/${ppId}`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const deleteProofPoint = createAsyncThunk<
  string,
  { domainId: string; ppId: string },
  { rejectValue: string }
>('brandMemory/deleteProofPoint', async ({ domainId, ppId }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/v1/domains/${domainId}/knowledge-base/proof-points/${ppId}`);
    return ppId;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

// ── Expert Voices ───────────────────────────────────────────────────

export const fetchExpertVoices = createAsyncThunk<ExpertVoice[], string, { rejectValue: string }>(
  'brandMemory/fetchExpertVoices',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/knowledge-base/expert-voices`);
      return unwrapList<ExpertVoice>(res.data, 'expert_voices');
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const createExpertVoice = createAsyncThunk<
  ExpertVoice,
  { domainId: string; payload: CreateExpertVoicePayload },
  { rejectValue: string }
>('brandMemory/createExpertVoice', async ({ domainId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post<ExpertVoice>(
      `/v1/domains/${domainId}/knowledge-base/expert-voices`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const updateExpertVoice = createAsyncThunk<
  ExpertVoice,
  { domainId: string; evId: string; payload: UpdateExpertVoicePayload },
  { rejectValue: string }
>('brandMemory/updateExpertVoice', async ({ domainId, evId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.patch<ExpertVoice>(
      `/v1/domains/${domainId}/knowledge-base/expert-voices/${evId}`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const deleteExpertVoice = createAsyncThunk<
  string,
  { domainId: string; evId: string },
  { rejectValue: string }
>('brandMemory/deleteExpertVoice', async ({ domainId, evId }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/v1/domains/${domainId}/knowledge-base/expert-voices/${evId}`);
    return evId;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const addQuote = createAsyncThunk<
  ExpertVoice,
  { domainId: string; evId: string; payload: CreateQuotePayload },
  { rejectValue: string }
>('brandMemory/addQuote', async ({ domainId, evId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post<ExpertVoice>(
      `/v1/domains/${domainId}/knowledge-base/expert-voices/${evId}/quotes`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const updateQuote = createAsyncThunk<
  ExpertVoice,
  { domainId: string; evId: string; qId: string; payload: UpdateQuotePayload },
  { rejectValue: string }
>('brandMemory/updateQuote', async ({ domainId, evId, qId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.patch<ExpertVoice>(
      `/v1/domains/${domainId}/knowledge-base/expert-voices/${evId}/quotes/${qId}`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const deleteQuote = createAsyncThunk<
  ExpertVoice,
  { domainId: string; evId: string; qId: string },
  { rejectValue: string }
>('brandMemory/deleteQuote', async ({ domainId, evId, qId }, { rejectWithValue }) => {
  try {
    const res = await apiClient.delete<ExpertVoice>(
      `/v1/domains/${domainId}/knowledge-base/expert-voices/${evId}/quotes/${qId}`
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

// ── Facts & Statistics ──────────────────────────────────────────────

export const fetchFacts = createAsyncThunk<FactStatistic[], string, { rejectValue: string }>(
  'brandMemory/fetchFacts',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/knowledge-base/facts-statistics`);
      return unwrapList<FactStatistic>(res.data, 'facts_statistics');
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const createFact = createAsyncThunk<
  FactStatistic,
  { domainId: string; payload: CreateFactPayload },
  { rejectValue: string }
>('brandMemory/createFact', async ({ domainId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post<FactStatistic>(
      `/v1/domains/${domainId}/knowledge-base/facts-statistics`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const updateFact = createAsyncThunk<
  FactStatistic,
  { domainId: string; fsId: string; payload: UpdateFactPayload },
  { rejectValue: string }
>('brandMemory/updateFact', async ({ domainId, fsId, payload }, { rejectWithValue }) => {
  try {
    const res = await apiClient.patch<FactStatistic>(
      `/v1/domains/${domainId}/knowledge-base/facts-statistics/${fsId}`,
      payload
    );
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const deleteFact = createAsyncThunk<
  string,
  { domainId: string; fsId: string },
  { rejectValue: string }
>('brandMemory/deleteFact', async ({ domainId, fsId }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/v1/domains/${domainId}/knowledge-base/facts-statistics/${fsId}`);
    return fsId;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

// ── Site Pages (read-only) ──────────────────────────────────────────

export const fetchSitePages = createAsyncThunk<SitePageEnriched[], string, { rejectValue: string }>(
  'brandMemory/fetchSitePages',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/knowledge-base/site-pages`);
      return unwrapList<SitePageEnriched>(res.data, 'site_pages');
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

// ── Build trigger + status ──────────────────────────────────────────

/** API response from POST /knowledge-base/build — just an ack, NOT a BuildStatus. */
interface BuildTriggerAck {
  status: 'accepted' | 'fallback_to_job' | 'error';
  via?: 'service' | 'job' | 'local_subprocess';
  service_status_code?: number;
  job_id?: string;
}

export const triggerBuild = createAsyncThunk<BuildTriggerAck, string, { rejectValue: string }>(
  'brandMemory/triggerBuild',
  async (domainId, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiClient.post<BuildTriggerAck>(
        `/v1/domains/${domainId}/knowledge-base/build`
      );
      // Pull the freshly-written status doc so the UI flips to "running"
      // immediately, without waiting for the polling tick.
      dispatch(fetchBuildStatus(domainId));
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const fetchBuildStatus = createAsyncThunk<BuildStatus, string, { rejectValue: string }>(
  'brandMemory/fetchBuildStatus',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<BuildStatus>(`/v1/domains/${domainId}/knowledge-base/build/status`);
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

// ── Brand Kit standalone refresh ────────────────────────────────────
//
// Separate from `triggerBuild` (full KB build). Hits the standalone
// endpoint `/brand-kit/build` which rebuilds ONLY the brand_kit
// subcollection with `force_rebuild=True`. The progress lives in its
// own Firestore status doc (`build_status/brand_kit`) so it doesn't
// fight with a parallel knowledge_base build's progress panel.

export const triggerBrandKitBuild = createAsyncThunk<
  BuildTriggerAck,
  string,
  { rejectValue: string }
>(
  'brandMemory/triggerBrandKitBuild',
  async (domainId, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiClient.post<BuildTriggerAck>(
        `/v1/domains/${domainId}/brand-kit/build`
      );
      // Pull the freshly-written status doc so the UI flips to "running"
      // immediately, without waiting for the polling tick.
      dispatch(fetchBrandKitBuildStatus(domainId));
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const fetchBrandKitBuildStatus = createAsyncThunk<
  BuildStatus,
  string,
  { rejectValue: string }
>(
  'brandMemory/fetchBrandKitBuildStatus',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<BuildStatus>(
        `/v1/domains/${domainId}/brand-kit/build/status`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);


// ── Site corpus ─────────────────────────────────────────────────────

const corpusBase = (domainId: string) => `/v1/domains/${domainId}/site-corpus`;

export const fetchSiteCorpusManifest = createAsyncThunk<
  SiteCorpusManifest,
  string,
  { rejectValue: string }
>(
  'brandMemory/fetchSiteCorpusManifest',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<SiteCorpusManifest>(corpusBase(domainId));
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export interface FetchSiteCorpusPagesArgs {
  domainId: string;
  role?: SiteCorpusRole | 'content' | 'any';
  q?: string;
  /** Two-letter code; empty or absent means every language. */
  lang?: string;
  /** Present only when loading a further page: the first page REPLACES the
   *  list, the following ones append to it. */
  cursor?: string;
}

export const fetchSiteCorpusPages = createAsyncThunk<
  SiteCorpusPagesResponse & { appended: boolean },
  FetchSiteCorpusPagesArgs,
  { rejectValue: string }
>(
  'brandMemory/fetchSiteCorpusPages',
  async ({ domainId, role, q, lang, cursor }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (role && role !== 'any') params.set('role', role);
      if (q) params.set('q', q);
      if (lang) params.set('lang', lang);
      if (cursor) params.set('cursor', cursor);
      const res = await apiClient.get<SiteCorpusPagesResponse>(
        `${corpusBase(domainId)}/pages?${params.toString()}`
      );
      return { ...res.data, appended: Boolean(cursor) };
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const fetchSiteCorpusPage = createAsyncThunk<
  SiteCorpusPageDetail,
  { domainId: string; urlHash: string },
  { rejectValue: string }
>(
  'brandMemory/fetchSiteCorpusPage',
  async ({ domainId, urlHash }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<SiteCorpusPageDetail>(
        `${corpusBase(domainId)}/pages/${urlHash}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const triggerSiteCorpusBuild = createAsyncThunk<
  BuildTriggerAck,
  { domainId: string; mode?: 'incremental' | 'full' },
  { rejectValue: string }
>(
  'brandMemory/triggerSiteCorpusBuild',
  async ({ domainId, mode = 'incremental' }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiClient.post<BuildTriggerAck>(
        `${corpusBase(domainId)}/build`,
        { mode }
      );
      // Pull the freshly-written status doc so the panel flips to "running"
      // without waiting for the first polling tick.
      dispatch(fetchSiteCorpusBuildStatus(domainId));
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const fetchSiteCorpusBuildStatus = createAsyncThunk<
  BuildStatus,
  string,
  { rejectValue: string }
>(
  'brandMemory/fetchSiteCorpusBuildStatus',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<BuildStatus>(`${corpusBase(domainId)}/build/status`);
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const setSiteCorpusAutoRefresh = createAsyncThunk<
  { auto_refresh: boolean },
  { domainId: string; autoRefresh: boolean },
  { rejectValue: string }
>(
  'brandMemory/setSiteCorpusAutoRefresh',
  async ({ domainId, autoRefresh }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch<{ auto_refresh: boolean }>(
        corpusBase(domainId),
        { auto_refresh: autoRefresh }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);


// ── Site identity (the schema.org card) ─────────────────────────────

export const fetchSiteIdentity = createAsyncThunk<
  SiteIdentity,
  string,
  { rejectValue: string }
>(
  'brandMemory/fetchSiteIdentity',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<SiteIdentity>(`${corpusBase(domainId)}/identity`);
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
  { condition: notInShowcase }
);

export const proposeSiteIdentity = createAsyncThunk<
  SiteIdentityProposal,
  string,
  { rejectValue: string }
>(
  'brandMemory/proposeSiteIdentity',
  async (domainId, { rejectWithValue }) => {
    try {
      // Synchronous on the engine side: a fetch and two model calls.
      const res = await apiClient.post<SiteIdentityProposal>(
        `${corpusBase(domainId)}/identity/propose`, {}, { timeout: 120_000 }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const saveSiteIdentity = createAsyncThunk<
  SiteIdentity,
  { domainId: string; payload: SaveSiteIdentityPayload },
  { rejectValue: string }
>(
  'brandMemory/saveSiteIdentity',
  async ({ domainId, payload }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put<SiteIdentity>(`${corpusBase(domainId)}/identity`, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);


// ── Smart Import status (live progress) ─────────────────────────────

export type ImportPhase = 'idle' | 'starting' | 'extracting' | 'verifying' | 'saving' | 'completed' | 'failed';

export interface ImportStatus {
  phase: ImportPhase;
  completed?: number;
  total?: number;
  label?: string | null;
  updated_at?: string | null;
}

export const fetchImportStatus = createAsyncThunk<
  { assetType: SmartImportAssetType; status: ImportStatus },
  { domainId: string; assetType: SmartImportAssetType },
  { rejectValue: string }
>('brandMemory/fetchImportStatus', async ({ domainId, assetType }, { rejectWithValue }) => {
  try {
    const res = await apiClient.get<ImportStatus>(
      `/v1/domains/${domainId}/knowledge-base/import/${assetType}/status`
    );
    return { assetType, status: res.data };
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

// ── Smart Import (assisted asset extraction from text + files) ──────

export type SmartImportAssetType =
  | 'proof_points'
  | 'expert_voices'
  | 'facts_statistics';

export interface SmartImportFileSummary {
  filename: string;
  chars: number;
  truncated: boolean;
  error?: string | null;
}

export interface SmartImportResult {
  /** For singleton stance: the saved doc. For list types: list of saved records. */
  saved: unknown;
  /** Candidate snippets that failed verification. */
  rejected: string[];
  /** Per-file parse summary. */
  file_summaries: SmartImportFileSummary[];
  /** Engine-side surface error if extraction itself failed (vs. just empty results). */
  error?: string;
}

export interface SmartImportArgs {
  domainId: string;
  assetType: SmartImportAssetType;
  text: string;
  files: File[];
  /** Required true — confirms the user has the right to import this material.
   *  The server rejects the request with 400 if false/missing. */
  userAttestation: boolean;
  /** When true (default), the engine rewrites each verified claim into a
   *  publishable sentence in the same language as the input. When false, the
   *  verbatim text is saved as-is. */
  polish: boolean;
}

export const smartImport = createAsyncThunk<
  SmartImportResult,
  SmartImportArgs,
  { rejectValue: string }
>('brandMemory/smartImport', async ({ domainId, assetType, text, files, userAttestation, polish }, { rejectWithValue, dispatch }) => {
  const form = new FormData();
  form.append('text', text);
  // FastAPI Form(bool) parses 'true'/'false' (case insensitive) — be explicit
  // to avoid relying on Python's truthy-string fallback.
  form.append('user_attestation', userAttestation ? 'true' : 'false');
  form.append('polish', polish ? 'true' : 'false');
  for (const f of files) form.append('files', f, f.name);

  try {
    const res = await apiClient.post<SmartImportResult>(
      `/v1/domains/${domainId}/knowledge-base/import/${assetType}`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Extraction is synchronous on the engine. Allow a generous client-side
        // timeout so long blobs / multi-file requests don't get cut off.
        timeout: 120_000,
      }
    );
    // Refetch the affected list so the new assets appear immediately.
    if (assetType === 'proof_points') dispatch(fetchProofPoints(domainId));
    else if (assetType === 'expert_voices') dispatch(fetchExpertVoices(domainId));
    else if (assetType === 'facts_statistics') dispatch(fetchFacts(domainId));
    return res.data;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

// ── Brand Memory auto-classify (Notion block notebook) ──────────────

export interface BrandMemoryClassifyArgs {
  domainId: string;
  text: string;
  files: File[];
  userAttestation: boolean;
  polish: boolean;
}

export interface ClassifyBlock {
  asset_type: SmartImportAssetType;
  /** The Firestore record as saved (shape depends on asset_type). */
  record: Record<string, unknown>;
}

export interface BrandMemoryClassifyResult {
  blocks: ClassifyBlock[];
  rejected: string[];
  summary: Partial<Record<SmartImportAssetType, number>>;
  file_summaries?: SmartImportFileSummary[];
  error?: string;
}

export const classifyBrandMemory = createAsyncThunk<
  BrandMemoryClassifyResult,
  BrandMemoryClassifyArgs,
  { rejectValue: string }
>(
  'brandMemory/classify',
  async ({ domainId, text, files, userAttestation, polish }, { rejectWithValue, dispatch }) => {
    const form = new FormData();
    form.append('text', text);
    form.append('user_attestation', userAttestation ? 'true' : 'false');
    form.append('polish', polish ? 'true' : 'false');
    for (const f of files) form.append('files', f, f.name);

    try {
      const res = await apiClient.post<BrandMemoryClassifyResult>(
        `/v1/domains/${domainId}/brand-memory/classify`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Classify can take up to ~120s (Pro split + N extractor calls).
          timeout: 180_000,
        }
      );
      // Refetch all four collections so any tab the user switches to is
      // immediately up-to-date with the new blocks. Cheap reads, big UX win.
      dispatch(fetchProofPoints(domainId));
      dispatch(fetchExpertVoices(domainId));
      dispatch(fetchFacts(domainId));
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

// ── Undo recent SmartImport (bulk soft-delete) ──────────────────────
//
// Powers the "Undo" banner shown immediately after a `classifyBrandMemory`
// call. Calls the bulk-undo endpoint (cap 600s server-side) and refetches
// the three asset collections so the canvas re-renders without the
// just-undone records.

export interface UndoRecentSmartImportResult {
  deleted_count: number;
  deleted: { asset_type: string; id: string }[];
}

export const undoRecentSmartImport = createAsyncThunk<
  UndoRecentSmartImportResult,
  { domainId: string; withinSeconds?: number },
  { rejectValue: string }
>(
  'brandMemory/undoRecentSmartImport',
  async ({ domainId, withinSeconds = 60 }, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiClient.post<UndoRecentSmartImportResult>(
        `/v1/domains/${domainId}/brand-memory/undo-recent?within_seconds=${withinSeconds}`,
        null,
        { timeout: 15_000 }
      );
      // Refetch so the canvas drops the soft-deleted records.
      dispatch(fetchProofPoints(domainId));
      dispatch(fetchExpertVoices(domainId));
      dispatch(fetchFacts(domainId));
      return res.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);


// ── Change category (proof_point ↔ fact_statistic) ─────────────────
//
// Idempotency: the frontend generates a fresh `clientRequestId` per click
// (UUID v4 via crypto.randomUUID). The same id retried after a network
// flake returns the same target record — no duplicates. Double-tap on the
// Move button is also safe: the second dispatch hits the same UUID.
//
// Optimistic UX is implemented in the slice reducers below: the source
// disappears from its list and the target appears in the destination list
// the moment the thunk fulfils, with no extra refetch round-trip.

export type ChangeCategorySourceType = 'proof_point' | 'fact_statistic';
export type ChangeCategoryTargetType = 'proof_point' | 'fact_statistic';

export interface ChangeCategoryProofOverrides {
  title: string;
  detail?: string;
  type?: 'case_study' | 'data_point' | 'certification' | 'award' | 'partnership';
  client_name?: string;
  numbers?: string;
  vertical?: string;
}

export interface ChangeCategoryFactOverrides {
  fact?: string;
  source?: string;
  date?: string;
}

export interface ChangeCategoryArgs {
  domainId: string;
  sourceType: ChangeCategorySourceType;
  sourceId: string;
  targetType: ChangeCategoryTargetType;
  proofPointOverrides?: ChangeCategoryProofOverrides;
  factStatisticOverrides?: ChangeCategoryFactOverrides;
}

export interface ChangeCategoryResult {
  old: ProofPoint | FactStatistic | null;
  new: ProofPoint | FactStatistic;
  lossy_fields: string[];
  idempotent_replay: boolean;
  /** Mirrors what we passed in. The slice uses these to decide which
   *  client-side lists to mutate without having to inspect the records. */
  sourceType: ChangeCategorySourceType;
  targetType: ChangeCategoryTargetType;
  sourceId: string;
}

/** Generate a stable idempotency id. We expose a helper so components and
 *  tests can use the same code path. crypto.randomUUID is available in
 *  every browser we ship (and Node 19+ for tests). */
export function newClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Last-resort fallback. Not cryptographically strong, but the backend
  // only needs uniqueness within ~minutes, not security.
  return `cr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const changeAssetCategory = createAsyncThunk<
  ChangeCategoryResult,
  ChangeCategoryArgs & { clientRequestId?: string },
  { rejectValue: string }
>(
  'brandMemory/changeAssetCategory',
  async (
    { domainId, sourceType, sourceId, targetType, proofPointOverrides, factStatisticOverrides, clientRequestId },
    { rejectWithValue }
  ) => {
    const reqId = clientRequestId || newClientRequestId();
    try {
      const res = await apiClient.post<{
        old: ProofPoint | FactStatistic | null;
        new: ProofPoint | FactStatistic;
        lossy_fields: string[];
        idempotent_replay: boolean;
      }>(
        `/v1/domains/${domainId}/knowledge-base/${sourceType}/${sourceId}/change-category`,
        {
          target_type: targetType,
          client_request_id: reqId,
          proof_point_overrides: proofPointOverrides,
          fact_statistic_overrides: factStatisticOverrides,
        }
      );
      return { ...res.data, sourceType, targetType, sourceId };
    } catch (err) {
      // 409 already_superseded → surface a friendly message and let the
      // UI offer a refresh CTA. Other errors fall through to the default
      // errorMessage helper.
      const e = err as {
        response?: { status?: number; data?: { detail?: { message?: string; error?: string } | string } };
      };
      if (e.response?.status === 409) {
        const d = e.response?.data?.detail;
        const message = typeof d === 'object' ? d?.message : d;
        return rejectWithValue(message || 'This asset was moved by another tab. Refresh to continue.');
      }
      return rejectWithValue(errorMessage(err));
    }
  }
);

// ── Bulk asset status updates (earned-asset review) ─────────────────
//
// The Brand Memory build can produce "earned" assets (extracted from
// external web search) that arrive with `status: 'needs_review'`. The
// notebook offers Approve-all / Reject-all toolbars; this thunk fans
// out N PATCH calls with bounded concurrency (no thundering herd) and
// then re-fetches the affected list so Redux state matches the server
// authoritatively. We deliberately avoid a massive optimistic update —
// simple and robust beats clever-but-fragile.

/** Asset types that can carry a `status` field and therefore participate
 *  in earned-asset review. competitive_stance and voice_profile don't
 *  have per-record status (singletons), so they're excluded. */
export type ReviewableAssetType = 'proof_points' | 'expert_voices' | 'facts_statistics';

const BULK_STATUS_CONCURRENCY = 10;

async function runWithConcurrency<T, R>(
  items: T[],
  worker: (item: T) => Promise<R>,
  concurrency: number
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  const next = async (): Promise<void> => {
    while (true) {
      const idx = cursor++;
      if (idx >= items.length) return;
      results[idx] = await worker(items[idx]);
    }
  };
  const runners: Array<Promise<void>> = [];
  const n = Math.min(concurrency, items.length);
  for (let i = 0; i < n; i++) runners.push(next());
  await Promise.all(runners);
  return results;
}

export interface BulkUpdateAssetStatusArgs {
  domainId: string;
  assetType: ReviewableAssetType;
  assetIds: string[];
  newStatus: StatusType;
}

export interface BulkUpdateAssetStatusResult {
  assetType: ReviewableAssetType;
  /** Ids that succeeded. */
  succeeded: string[];
  /** Ids that failed (server error, 404, etc.). */
  failed: string[];
}

export const bulkUpdateAssetStatus = createAsyncThunk<
  BulkUpdateAssetStatusResult,
  BulkUpdateAssetStatusArgs,
  { rejectValue: string }
>(
  'brandMemory/bulkUpdateAssetStatus',
  async ({ domainId, assetType, assetIds, newStatus }, { dispatch, rejectWithValue }) => {
    if (assetIds.length === 0) {
      return { assetType, succeeded: [], failed: [] };
    }
    try {
      const succeeded: string[] = [];
      const failed: string[] = [];
      const endpointRoot = (() => {
        switch (assetType) {
          case 'proof_points':
            return `/v1/domains/${domainId}/knowledge-base/proof-points`;
          case 'expert_voices':
            return `/v1/domains/${domainId}/knowledge-base/expert-voices`;
          case 'facts_statistics':
            return `/v1/domains/${domainId}/knowledge-base/facts-statistics`;
        }
      })();
      await runWithConcurrency(
        assetIds,
        async (id) => {
          try {
            await apiClient.patch(`${endpointRoot}/${id}`, { status: newStatus });
            succeeded.push(id);
          } catch {
            failed.push(id);
          }
        },
        BULK_STATUS_CONCURRENCY
      );
      // Re-fetch the affected list so Redux state mirrors the server.
      // One fetch per type — earned bulk-review only operates on a
      // single list at a time.
      if (assetType === 'proof_points') dispatch(fetchProofPoints(domainId));
      else if (assetType === 'expert_voices') dispatch(fetchExpertVoices(domainId));
      else if (assetType === 'facts_statistics') dispatch(fetchFacts(domainId));
      return { assetType, succeeded, failed };
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────

const slice = createSlice({
  name: 'brandMemory',
  initialState,
  reducers: {
    clearSiteIdentityProposal(state) {
      state.siteIdentityProposal = null;
      state.siteIdentityError = null;
    },
    resetBrandMemory: () => initialState,
    clearBrandKitsError: (state) => {
      state.brandKitsError = null;
    },
    /** Reset the live import-status slot for one asset type. The notebook
     *  calls this on submit-start so the bar doesn't briefly display stale
     *  "completed · 5/5" from the previous run. */
    clearImportStatus: (state, action: PayloadAction<SmartImportAssetType>) => {
      delete state.importStatusByType[action.payload];
    },
    /** Dismiss the "moved to X" toast/banner. The notebook calls this on
     *  toast click-through and after a few seconds via setTimeout. */
    clearLastChangeCategory: (state) => {
      state.lastChangeCategory = null;
    },
  },
  extraReducers: (builder) => {
    // Brand Kits
    builder.addCase(fetchBrandKits.pending, (state) => {
      state.brandKitsStatus = 'loading';
      state.brandKitsError = null;
    });
    builder.addCase(fetchBrandKits.fulfilled, (state, action) => {
      state.brandKitsStatus = 'succeeded';
      state.brandKits = action.payload;
    });
    builder.addCase(fetchBrandKits.rejected, (state, action) => {
      state.brandKitsStatus = 'failed';
      state.brandKitsError = action.payload || action.error.message || 'Failed to load Brand Kits';
    });
    builder.addCase(createBrandKit.fulfilled, (state, action: PayloadAction<BrandKit>) => {
      state.brandKits.push(action.payload);
    });
    builder.addCase(updateBrandKit.fulfilled, (state, action: PayloadAction<BrandKit>) => {
      const idx = state.brandKits.findIndex((k) => k.id === action.payload.id);
      if (idx >= 0) state.brandKits[idx] = action.payload;
    });
    builder.addCase(deleteBrandKit.fulfilled, (state, action: PayloadAction<string>) => {
      state.brandKits = state.brandKits.filter((k) => k.id !== action.payload);
    });
    builder.addCase(setPrimaryBrandKit.fulfilled, (state, action: PayloadAction<BrandKit[]>) => {
      state.brandKits = action.payload;
    });

    // Proof Points
    builder.addCase(fetchProofPoints.pending, (state) => {
      state.proofPointsStatus = 'loading';
      state.proofPointsError = null;
    });
    builder.addCase(fetchProofPoints.fulfilled, (state, action) => {
      state.proofPointsStatus = 'succeeded';
      state.proofPoints = action.payload;
    });
    builder.addCase(fetchProofPoints.rejected, (state, action) => {
      state.proofPointsStatus = 'failed';
      state.proofPointsError = action.payload || action.error.message || 'Failed to load proof points';
    });
    builder.addCase(createProofPoint.fulfilled, (state, action: PayloadAction<ProofPoint>) => {
      state.proofPoints.unshift(action.payload);
    });
    builder.addCase(updateProofPoint.fulfilled, (state, action: PayloadAction<ProofPoint>) => {
      const idx = state.proofPoints.findIndex((p) => p.id === action.payload.id);
      if (idx >= 0) state.proofPoints[idx] = action.payload;
    });
    builder.addCase(deleteProofPoint.fulfilled, (state, action: PayloadAction<string>) => {
      state.proofPoints = state.proofPoints.filter((p) => p.id !== action.payload);
    });

    // Expert Voices
    builder.addCase(fetchExpertVoices.pending, (state) => {
      state.expertVoicesStatus = 'loading';
      state.expertVoicesError = null;
    });
    builder.addCase(fetchExpertVoices.fulfilled, (state, action) => {
      state.expertVoicesStatus = 'succeeded';
      state.expertVoices = action.payload;
    });
    builder.addCase(fetchExpertVoices.rejected, (state, action) => {
      state.expertVoicesStatus = 'failed';
      state.expertVoicesError = action.payload || action.error.message || 'Failed to load expert voices';
    });
    builder.addCase(createExpertVoice.fulfilled, (state, action: PayloadAction<ExpertVoice>) => {
      state.expertVoices.unshift(action.payload);
    });
    builder.addCase(updateExpertVoice.fulfilled, (state, action: PayloadAction<ExpertVoice>) => {
      const idx = state.expertVoices.findIndex((e) => e.id === action.payload.id);
      if (idx >= 0) state.expertVoices[idx] = action.payload;
    });
    builder.addCase(deleteExpertVoice.fulfilled, (state, action: PayloadAction<string>) => {
      state.expertVoices = state.expertVoices.filter((e) => e.id !== action.payload);
    });
    builder.addCase(addQuote.fulfilled, (state, action: PayloadAction<ExpertVoice>) => {
      const idx = state.expertVoices.findIndex((e) => e.id === action.payload.id);
      if (idx >= 0) state.expertVoices[idx] = action.payload;
    });
    builder.addCase(updateQuote.fulfilled, (state, action: PayloadAction<ExpertVoice>) => {
      const idx = state.expertVoices.findIndex((e) => e.id === action.payload.id);
      if (idx >= 0) state.expertVoices[idx] = action.payload;
    });
    builder.addCase(deleteQuote.fulfilled, (state, action: PayloadAction<ExpertVoice>) => {
      const idx = state.expertVoices.findIndex((e) => e.id === action.payload.id);
      if (idx >= 0) state.expertVoices[idx] = action.payload;
    });

    // Facts
    builder.addCase(fetchFacts.pending, (state) => {
      state.factsStatus = 'loading';
      state.factsError = null;
    });
    builder.addCase(fetchFacts.fulfilled, (state, action) => {
      state.factsStatus = 'succeeded';
      state.facts = action.payload;
    });
    builder.addCase(fetchFacts.rejected, (state, action) => {
      state.factsStatus = 'failed';
      state.factsError = action.payload || action.error.message || 'Failed to load facts';
    });
    builder.addCase(createFact.fulfilled, (state, action: PayloadAction<FactStatistic>) => {
      state.facts.unshift(action.payload);
    });
    builder.addCase(updateFact.fulfilled, (state, action: PayloadAction<FactStatistic>) => {
      const idx = state.facts.findIndex((f) => f.id === action.payload.id);
      if (idx >= 0) state.facts[idx] = action.payload;
    });
    builder.addCase(deleteFact.fulfilled, (state, action: PayloadAction<string>) => {
      state.facts = state.facts.filter((f) => f.id !== action.payload);
    });

    // Site Pages
    builder.addCase(fetchSitePages.pending, (state) => {
      state.sitePagesStatus = 'loading';
    });
    builder.addCase(fetchSitePages.fulfilled, (state, action) => {
      state.sitePagesStatus = 'succeeded';
      state.sitePages = action.payload;
    });

    // Build status
    builder.addCase(triggerBuild.pending, (state) => {
      state.buildTriggering = true;
      state.buildTriggerError = null;
    });
    builder.addCase(triggerBuild.fulfilled, (state) => {
      // The ack doesn't carry the run state — fetchBuildStatus (chained inside
      // the thunk) populates `buildStatus` with the real Firestore doc.
      state.buildTriggering = false;
    });
    builder.addCase(triggerBuild.rejected, (state, action) => {
      state.buildTriggering = false;
      state.buildTriggerError = action.payload || action.error.message || 'Failed to trigger build';
    });
    builder.addCase(fetchBuildStatus.pending, (state) => {
      state.buildStatusLoadable = 'loading';
    });
    builder.addCase(fetchBuildStatus.fulfilled, (state, action) => {
      state.buildStatusLoadable = 'succeeded';
      state.buildStatus = action.payload;
    });
    builder.addCase(fetchBuildStatus.rejected, (state) => {
      state.buildStatusLoadable = 'failed';
    });

    // Brand Kit standalone build status (mirrors the KB triplet above,
    // but stored on its own slice fields so the two builds can be
    // observed independently by the UI).
    builder.addCase(triggerBrandKitBuild.pending, (state) => {
      state.brandKitBuildTriggering = true;
      state.brandKitBuildTriggerError = null;
    });
    builder.addCase(triggerBrandKitBuild.fulfilled, (state) => {
      state.brandKitBuildTriggering = false;
    });
    builder.addCase(triggerBrandKitBuild.rejected, (state, action) => {
      state.brandKitBuildTriggering = false;
      state.brandKitBuildTriggerError =
        action.payload || action.error.message || 'Failed to refresh Brand Kit';
    });
    builder.addCase(fetchBrandKitBuildStatus.pending, (state) => {
      state.brandKitBuildStatusLoadable = 'loading';
    });
    builder.addCase(fetchBrandKitBuildStatus.fulfilled, (state, action) => {
      state.brandKitBuildStatusLoadable = 'succeeded';
      state.brandKitBuildStatus = action.payload;
    });
    builder.addCase(fetchBrandKitBuildStatus.rejected, (state) => {
      state.brandKitBuildStatusLoadable = 'failed';
    });

    builder.addCase(fetchImportStatus.fulfilled, (state, action) => {
      state.importStatusByType[action.payload.assetType] = action.payload.status;
    });

    // Change Category — atomic UI updates so the user sees a single
    // transition with no flicker. The reducer:
    //  - on pending: marks the source id as inflight (UI disables kebab
    //    + greys out the row).
    //  - on fulfilled: removes from the source list, inserts at the top
    //    of the target list, sets `lastChangeCategory` so the toast can
    //    show a "View in {target}" CTA.
    //  - on rejected: clears inflight (the row reappears as it was).
    builder.addCase(changeAssetCategory.pending, (state, action) => {
      const id = action.meta.arg.sourceId;
      if (!state.changeCategoryInflight.includes(id)) {
        state.changeCategoryInflight.push(id);
      }
    });
    builder.addCase(changeAssetCategory.fulfilled, (state, action) => {
      const { sourceType, targetType, sourceId, new: newRecord, lossy_fields } = action.payload;
      state.changeCategoryInflight = state.changeCategoryInflight.filter((x) => x !== sourceId);

      // Remove from source list
      if (sourceType === 'proof_point') {
        state.proofPoints = state.proofPoints.filter((p) => p.id !== sourceId);
      } else if (sourceType === 'fact_statistic') {
        state.facts = state.facts.filter((f) => f.id !== sourceId);
      }

      // Insert into target list at the top so the user sees the new
      // record immediately. We use unshift to mimic the create-asset
      // pattern used elsewhere in the slice.
      if (targetType === 'proof_point') {
        // Don't double-insert on idempotent replays.
        if (!state.proofPoints.some((p) => p.id === newRecord.id)) {
          state.proofPoints.unshift(newRecord as ProofPoint);
        }
      } else if (targetType === 'fact_statistic') {
        if (!state.facts.some((f) => f.id === newRecord.id)) {
          state.facts.unshift(newRecord as FactStatistic);
        }
      }

      state.lastChangeCategory = {
        sourceType,
        targetType,
        newId: newRecord.id,
        lossy_fields,
        at: Date.now(),
      };
    });
    builder.addCase(changeAssetCategory.rejected, (state, action) => {
      const id = action.meta.arg.sourceId;
      state.changeCategoryInflight = state.changeCategoryInflight.filter((x) => x !== id);
    });

    // ── Site corpus ────────────────────────────────────────────────
    builder.addCase(fetchSiteCorpusManifest.pending, (state) => {
      state.siteCorpusManifestStatus = 'loading';
    });
    builder.addCase(fetchSiteCorpusManifest.fulfilled, (state, action) => {
      state.siteCorpusManifest = action.payload;
      state.siteCorpusManifestStatus = 'succeeded';
    });
    builder.addCase(fetchSiteCorpusManifest.rejected, (state) => {
      state.siteCorpusManifestStatus = 'failed';
    });

    builder.addCase(fetchSiteCorpusPages.pending, (state, action) => {
      state.siteCorpusPagesStatus = 'loading';
      if (!action.meta.arg.cursor) {
        state.siteCorpusFilters = {
          role: action.meta.arg.role ?? state.siteCorpusFilters.role,
          q: action.meta.arg.q ?? '',
        };
      }
    });
    builder.addCase(fetchSiteCorpusPages.fulfilled, (state, action) => {
      const { items, next_cursor, appended } = action.payload;
      // A further page appends and dedupes; a first page replaces. Without
      // the dedupe a row can appear twice when a build writes while the
      // user is paging through the list.
      if (appended) {
        const seen = new Set(state.siteCorpusPages.map((p) => p.url_hash));
        state.siteCorpusPages = [
          ...state.siteCorpusPages,
          ...items.filter((p) => !seen.has(p.url_hash)),
        ];
      } else {
        state.siteCorpusPages = items;
      }
      state.siteCorpusNextCursor = next_cursor;
      state.siteCorpusPagesStatus = 'succeeded';
    });
    builder.addCase(fetchSiteCorpusPages.rejected, (state) => {
      state.siteCorpusPagesStatus = 'failed';
    });

    builder.addCase(fetchSiteCorpusPage.pending, (state) => {
      state.siteCorpusPageDetailStatus = 'loading';
    });
    builder.addCase(fetchSiteCorpusPage.fulfilled, (state, action) => {
      state.siteCorpusPageDetails[action.payload.url_hash] = action.payload;
      state.siteCorpusPageDetailStatus = 'succeeded';
    });
    builder.addCase(fetchSiteCorpusPage.rejected, (state) => {
      state.siteCorpusPageDetailStatus = 'failed';
    });

    builder.addCase(triggerSiteCorpusBuild.pending, (state) => {
      state.siteCorpusBuildTriggering = true;
      state.siteCorpusBuildTriggerError = null;
    });
    builder.addCase(triggerSiteCorpusBuild.fulfilled, (state) => {
      state.siteCorpusBuildTriggering = false;
    });
    builder.addCase(triggerSiteCorpusBuild.rejected, (state, action) => {
      state.siteCorpusBuildTriggering = false;
      state.siteCorpusBuildTriggerError = action.payload ?? 'Build failed to start';
    });

    builder.addCase(fetchSiteCorpusBuildStatus.pending, (state) => {
      state.siteCorpusBuildStatusLoadable = 'loading';
    });
    builder.addCase(fetchSiteCorpusBuildStatus.fulfilled, (state, action) => {
      state.siteCorpusBuildStatus = action.payload;
      state.siteCorpusBuildStatusLoadable = 'succeeded';
    });
    builder.addCase(fetchSiteCorpusBuildStatus.rejected, (state) => {
      state.siteCorpusBuildStatusLoadable = 'failed';
    });

    builder.addCase(setSiteCorpusAutoRefresh.fulfilled, (state, action) => {
      if (state.siteCorpusManifest) {
        state.siteCorpusManifest.auto_refresh = action.payload.auto_refresh;
      }
    });

    // ── Site identity ─────────────────────────────────────────────
    builder.addCase(fetchSiteIdentity.pending, (state) => {
      state.siteIdentityStatus = 'loading';
    });
    builder.addCase(fetchSiteIdentity.fulfilled, (state, action) => {
      state.siteIdentity = action.payload;
      state.siteIdentityStatus = 'succeeded';
    });
    builder.addCase(fetchSiteIdentity.rejected, (state) => {
      state.siteIdentityStatus = 'failed';
    });

    builder.addCase(proposeSiteIdentity.pending, (state) => {
      state.siteIdentityProposing = true;
      state.siteIdentityError = null;
    });
    builder.addCase(proposeSiteIdentity.fulfilled, (state, action) => {
      state.siteIdentityProposing = false;
      const draft = action.payload;
      const naming = state.siteIdentity?.naming ?? { name: '', alternate_names: [] };
      if (draft.saved) {
        // Stored as the draft: the card IS this now.
        state.siteIdentity = { ...draft, exists: true, naming, homepage_changed: false };
        state.siteIdentityProposal = null;
      } else {
        // An approved card stays; the proposal waits for the person.
        state.siteIdentityProposal = draft;
      }
    });
    builder.addCase(proposeSiteIdentity.rejected, (state, action) => {
      state.siteIdentityProposing = false;
      state.siteIdentityError = action.payload ?? 'The proposal did not come back';
    });

    builder.addCase(saveSiteIdentity.pending, (state) => {
      state.siteIdentitySaving = true;
      state.siteIdentityError = null;
    });
    builder.addCase(saveSiteIdentity.fulfilled, (state, action) => {
      state.siteIdentitySaving = false;
      const naming = state.siteIdentity?.naming ?? { name: '', alternate_names: [] };
      state.siteIdentity = { ...action.payload, exists: true, naming, homepage_changed: false };
      state.siteIdentityProposal = null;
    });
    builder.addCase(saveSiteIdentity.rejected, (state, action) => {
      state.siteIdentitySaving = false;
      state.siteIdentityError = action.payload ?? 'The card could not be saved';
    });
  },
});

export const { resetBrandMemory, clearBrandKitsError, clearImportStatus, clearLastChangeCategory, clearSiteIdentityProposal } = slice.actions;
export default slice.reducer;
